import { CRITERIA_ACCEPTABLE_QUESTION_TYPES } from '$app-web/constants';
import { ObjectType } from '$app-web/types';
import { SuperDate } from '$app-web/utils';
import { criteriaEvaluator } from '$app-web/utils/criteria-evaluator';

import { StoreQuestion } from '../store/types';
import { isAnswerValueEmpty, shouldPresentQuestion } from './helpers';

function validateCriteria(parameters: {
    criteria: string;
    tokens: ObjectType<{ type: string; value: unknown }>;
    disabledQuestions: Array<number>;
    registrationTime: number;
}) {
    const { criteria, tokens, disabledQuestions, registrationTime } = parameters;

    if (criteria == null || criteria === '') {
        return true;
    }

    const criteriaLower = criteria.toLowerCase();

    // 1. Handle 'registration-date-time' criteria
    if (criteriaLower.includes('_since_reg_')) {
        const currentDate = new Date();
        const differenceOfTimes = SuperDate.getDifference(currentDate, registrationTime);

        // Extract time resolution
        let timeResolution = '';
        if (criteriaLower.includes('seconds')) timeResolution = 'seconds';
        else if (criteriaLower.includes('minutes')) timeResolution = 'minutes';
        else if (criteriaLower.includes('hours')) timeResolution = 'hours';
        else if (criteriaLower.includes('days')) timeResolution = 'days';
        else if (criteriaLower.includes('weeks')) timeResolution = 'weeks';
        else if (criteriaLower.includes('months')) timeResolution = 'months';
        else if (criteriaLower.includes('years')) timeResolution = 'years';

        // Extract the numeric value and operator from the criteria
        // E.g., "_days_since_reg_date >= 3" should extract ">=" and "3"
        const regex = /([<>=!]+)\s*(\d+)/;
        const match = criteriaLower.match(regex);

        if (match && timeResolution) {
            const operator = match[1]; // e.g., ">="
            const value = parseInt(match[2], 10); // e.g., 3

            // Get the appropriate time difference based on the resolution
            const timeDifference = differenceOfTimes[timeResolution as keyof typeof differenceOfTimes] ?? 0;

            // Evaluate the comparison
            switch (operator) {
                case '>':
                    return timeDifference > value;
                case '>=':
                    return timeDifference >= value;
                case '<':
                    return timeDifference < value;
                case '<=':
                    return timeDifference <= value;
                case '==':
                case '=':
                    return timeDifference === value;
                case '!=':
                    return timeDifference !== value;
                default:
                    return false;
            }
        }
    }

    // 2. Handle 'question-value' criteria
    return criteriaEvaluator(criteria, (type: string, value: string) => {
        if (type === 'question-value') {
            const numericIdPart = Number(value.replace('Q', ''));
            const isSourceQuestionEnabled = !disabledQuestions.includes(numericIdPart);
            const item = tokens[value];

            if (isSourceQuestionEnabled && item != null && CRITERIA_ACCEPTABLE_QUESTION_TYPES.includes(item.type)) {
                return item.value;
            }
        }

        return undefined;
    });
}

export function applySurveyCriteria(params: {
    questions: Array<StoreQuestion>;
    activityId: number;
    tokens: ObjectType<{ type: string; value: unknown }>;
    registrationTime: number;
}): Array<StoreQuestion> {
    const { questions, activityId, tokens, registrationTime } = params;

    const disabledQuestions = questions
        .filter((question) => !shouldPresentQuestion(question))
        .map((question) => question.questionId);

    const questionsTokens = Object.fromEntries(
        questions
            .filter((question) => !isAnswerValueEmpty(question.answer))
            .map((question) => [[`Q${question.questionId}`], { type: question.questionType, value: question.answer }])
    ) as ObjectType<{ type: string; value: unknown }>;

    const newTokens = { ...tokens, ...questionsTokens };

    const updatedSections = questions.map((question) => {
        const { criteria = '', sectionCriteria = '', questionId } = question;

        const targetToken = `Q${question.questionId}`;
        const isCriteriaSelfPointer = criteria.includes(targetToken) && !criteria.includes(`${targetToken}_`);
        const finalCriteria = isCriteriaSelfPointer
            ? question.criteria.replaceAll(targetToken, `Q${activityId}_${questionId}`)
            : question.criteria;

        const commonProps = {
            disabledQuestions,
            registrationTime,
            tokens: newTokens,
        };

        return {
            ...question,
            isHiddenBySectionCriteria: !validateCriteria({ ...commonProps, criteria: sectionCriteria }),
            isHiddenByCriteria: !validateCriteria({ ...commonProps, criteria: finalCriteria }),
        };
    });

    return updatedSections;
}
